import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, Optional, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import jsQR from 'jsqr';
import { Subject, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { DialogUtils } from 'src/app/common/utils/dialog.utils';
import { BaseRespone } from 'src/app/models/common.response';
import { AbhaAddressSelectComponent } from './abha-address-select/abha-address-select.component';
import { AbhaCardComponent } from './abha-card/abha-card.component';
import { AbhaConsentComponent } from './abha-consent/abha-consent.component';
import { AbhaFindComponent } from './abha-find/abha-find.component';
import { AbhaMobileVerifyComponent } from './abha-mobile-verify/abha-mobile-verify.component';
import { AbhaOtpResendComponent } from './abha-otp-resend/abha-otp-resend.component';
import { ABHA_LOGIN_ID_PATTERN, resolveAbhaLoginType } from './abha-login.util';
import { extractMaskedMobile } from './abha-otp-message.util';
import { AbhaService } from './abha.service';
import { AbhaAcquisitionMethod, AbhaEnrolmentResult, AbhaOtpResult } from './iabha-info';
import { hasCompleteAbhaProfile, parseAbhaQr } from './abha-qr.util';

export type AbhaDialogMode = 'create' | 'verify' | 'find' | 'scan';

// Steps a new ABHA still has to go through before its details are shown.
export type AbhaEnrolStep = 'mobile' | 'address';

@Component({
  selector: 'app-abha-create-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    AbhaConsentComponent,
    AbhaOtpResendComponent,
    AbhaFindComponent,
    AbhaCardComponent,
    AbhaMobileVerifyComponent,
    AbhaAddressSelectComponent,
  ],
  templateUrl: './abha-create-dialog.component.html',
  styleUrl: './abha-create-dialog.component.scss',
})
export class AbhaCreateDialogComponent implements OnDestroy {
  abhaForm: FormGroup;
  verifyForm: FormGroup;
  mode: AbhaDialogMode = 'create';

  get cardOnly(): boolean {
    return !!this.data?.cardOnly;
  }
  txnId = '';
  otpNotice = '';
  isRequestingOtp = false;
  isEnrolling = false;
  enrolment: AbhaEnrolmentResult | null = null;
  enrolStep: AbhaEnrolStep | null = null;
  stepTxnId = '';
  scanError = '';
  @ViewChild('qrVideo') qrVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild(AbhaFindComponent) findComponent?: AbhaFindComponent;
  findIsBusy = false;
  findIsOtpSent = false;
  findHasAccounts = false;
  private mediaStream: MediaStream | null = null;
  private scanTimer: ReturnType<typeof setInterval> | null = null;
  private verifyAcquisitionMethod: AbhaAcquisitionMethod = 'ExistingOtp';
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private abhaService: AbhaService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<AbhaCreateDialogComponent>,
    private dialogUtils: DialogUtils,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: { mobile?: string; patientId?: number; cardOnly?: boolean; abhaNumber?: string },
  ) {
    // ABDM requires a communication mobile to create an ABHA. It is prefilled from the patient's
    // contact number when one exists, so the front desk does not type it twice.
    this.abhaForm = this.fb.group({
      aadhaarNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{12}$/)]],
      mobile: [this.data?.mobile || '', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      otp: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
      consent: this.fb.nonNullable.control(false, Validators.requiredTrue),
    });
    this.mode = this.cardOnly ? 'verify' : 'create';
    this.verifyForm = this.fb.group({
      loginId: [this.cardOnly ? this.data?.abhaNumber || '' : '', [Validators.required, Validators.pattern(ABHA_LOGIN_ID_PATTERN)]],
      otpSystem: ['abdm', Validators.required],
      otp: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
    });
  }

  get consentControl(): FormControl<boolean> {
    return this.abhaForm.get('consent') as FormControl<boolean>;
  }

  private get verifyLoginId(): string {
    return String(this.verifyForm.get('loginId')?.value).trim();
  }

  get isOtpSent(): boolean {
    return this.txnId.length > 0;
  }

  get isBusy(): boolean {
    return this.isRequestingOtp || this.isEnrolling || (this.mode === 'find' && this.findIsBusy);
  }

  syncFindState(): void {
    this.findIsBusy = !!this.findComponent?.isBusy;
    this.findIsOtpSent = !!this.findComponent?.isOtpSent;
    this.findHasAccounts = !!this.findComponent?.hasAccounts;
  }

  setMode(mode: AbhaDialogMode): void {
    if (this.cardOnly || this.isBusy || this.enrolment || this.mode === mode) {
      return;
    }

    this.mode = mode;
    this.findIsBusy = false;
    this.findIsOtpSent = false;
    this.findHasAccounts = false;
    this.verifyAcquisitionMethod = 'ExistingOtp';
    this.txnId = '';
    this.otpNotice = '';
    this.scanError = '';
    this.abhaForm.get('otp')?.disable();
    this.consentControl.enable();
    this.verifyForm.get('otp')?.disable();
    this.stopScanner();
    if (mode === 'scan') {
      this.startScanner();
    }
  }

  onSendOtp(): void {
    if (this.createDetailsInvalid()) {
      this.abhaForm.markAllAsTouched();
      return;
    }

    this.isRequestingOtp = true;
    this.abhaService
      .requestOtp(this.abhaForm.get('aadhaarNumber')?.value, this.data?.patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => this.handleOtpResponse(response));
  }

  onVerify(): void {
    if (this.abhaForm.invalid) {
      this.abhaForm.markAllAsTouched();
      return;
    }

    this.isEnrolling = true;
    this.abhaService
      .enrolByAadhaar({
        txnId: this.txnId,
        otp: this.abhaForm.get('otp')?.value,
        mobile: this.abhaForm.get('mobile')?.value,
        patientId: this.data?.patientId
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => this.handleEnrolResponse(response));
  }

  onSendLoginOtp(): void {
    const loginId = this.verifyForm.get('loginId');
    if (!loginId || loginId.invalid) {
      this.verifyForm.markAllAsTouched();
      return;
    }

    this.isRequestingOtp = true;
    this.abhaService
      .requestLoginOtp({
        loginType: resolveAbhaLoginType(this.verifyLoginId),
        loginId: this.verifyLoginId,
        otpSystem: this.verifyForm.get('otpSystem')?.value
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => this.handleLoginOtpResponse(response));
  }

  onVerifyLogin(): void {
    if (this.verifyForm.invalid) {
      this.verifyForm.markAllAsTouched();
      return;
    }

    this.isEnrolling = true;
    this.abhaService
      .verifyLoginOtp({
        loginType: resolveAbhaLoginType(this.verifyLoginId),
        txnId: this.txnId,
        otp: this.verifyForm.get('otp')?.value,
        otpSystem: this.verifyForm.get('otpSystem')?.value,
        patientId: this.cardOnly ? undefined : this.data?.patientId,
        acquisitionMethod: this.verifyAcquisitionMethod
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => this.handleEnrolResponse(response));
  }

  onAbhaFound(found: AbhaEnrolmentResult): void {
    if (this.closeIfAlreadyLinked(found)) {
      return;
    }

    this.enrolment = { ...found, acquisitionMethod: 'ExistingOtp' };
    this.toastr.success(this.translate.instant('ABHA.VERIFIED_SUCCESS'));
  }

  // No ABHA was found: move to the create tab, keeping the Aadhaar number already typed.
  onCreateRequested(aadhaarNumber: string | undefined): void {
    this.setMode('create');
    if (aadhaarNumber) {
      this.abhaForm.patchValue({ aadhaarNumber });
    }
  }

  onMobileVerified(txnId: string): void {
    if (this.enrolment) {
      this.enrolment = { ...this.enrolment, mobile: this.abhaForm.get('mobile')?.value };
    }

    this.stepTxnId = txnId;
    this.enrolStep = 'address';
  }

  onAddressSelected(abhaAddress: string): void {
    if (this.enrolment) {
      this.enrolment = { ...this.enrolment, abhaAddress };
    }

    this.enrolStep = null;
  }

  onCancel(): void {
    if (this.isBusy) {
      return;
    }
    this.dialogUtils.closeSidePanel(this.dialogRef, this.cardOnly || this.enrolStep ? null : this.enrolment);
  }

  ngOnDestroy(): void {
    this.stopScanner();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createDetailsInvalid(): boolean {
    const aadhaar = this.abhaForm.get('aadhaarNumber');
    const mobile = this.abhaForm.get('mobile');
    return !aadhaar || aadhaar.invalid || !mobile || mobile.invalid || this.consentControl.invalid;
  }

  private handleOtpResponse(response: BaseRespone<AbhaOtpResult>): void {
    this.isRequestingOtp = false;

    if (response && response.isSuccess && response.result?.txnId) {
      this.txnId = response.result.txnId;
      this.otpNotice = this.buildCreateOtpNotice(response.result.message);
      this.abhaForm.get('otp')?.enable();
      this.consentControl.disable();
      this.toastr.success(this.translate.instant('ABHA.OTP_SENT'));
      return;
    }

    this.toastr.error(this.resolveError(response, this.translate.instant('ABHA.OTP_FAILED')));
  }

  // Wording from NHA M1 CRT_ABHA_105, with the masked number ABDM returned.
  private buildCreateOtpNotice(abdmMessage: string): string {
    const maskedMobile = extractMaskedMobile(abdmMessage);
    if (!maskedMobile) {
      return abdmMessage || '';
    }

    return this.translate.instant('ABHA.OTP_NOTICE_CREATE', { mobile: maskedMobile });
  }

  private handleLoginOtpResponse(response: BaseRespone<AbhaOtpResult>): void {
    this.isRequestingOtp = false;

    if (response && response.isSuccess && response.result?.txnId) {
      this.txnId = response.result.txnId;
      this.otpNotice = response.result.message || '';
      this.verifyForm.get('otp')?.enable();
      this.toastr.success(this.translate.instant('ABHA.OTP_SENT_LOGIN'));
      return;
    }

    this.toastr.error(this.resolveError(response, this.translate.instant('ABHA.OTP_FAILED')));
  }

  private handleEnrolResponse(response: BaseRespone<AbhaEnrolmentResult>): void {
    this.isEnrolling = false;

    if (response && response.isSuccess && response.result?.abhaNumber) {
      if (this.cardOnly && response.result.abhaNumber.replace(/\D/g, '') !== this.data?.abhaNumber?.replace(/\D/g, '')) {
        this.toastr.error(this.translate.instant('ABHA.CARD_ABHA_MISMATCH'));
        return;
      }

      if (!this.cardOnly && this.closeIfAlreadyLinked(response.result)) {
        return;
      }

      this.enrolment = {
        ...response.result,
        acquisitionMethod: this.resolveAcquisitionMethod(response.result)
      };
      const successKey = response.result.isNew ? 'ABHA.CREATED_SUCCESS' : 'ABHA.VERIFIED_SUCCESS';
      this.toastr.success(this.translate.instant(successKey));
      this.startEnrolSteps(response.result);
      return;
    }

    this.toastr.error(this.resolveError(response, this.translate.instant('ABHA.CREATE_FAILED')));
  }

  // A new ABHA goes on to verify its mobile (when it is not the Aadhaar mobile) and to
  // choose its ABHA address. An existing ABHA shows its details straight away.
  private startEnrolSteps(result: AbhaEnrolmentResult): void {
    if (this.mode !== 'create' || !result.isNew || !result.txnId) {
      return;
    }

    this.stepTxnId = result.txnId;
    this.enrolStep = result.isMobileVerificationRequired ? 'mobile' : 'address';
  }

  private resolveAcquisitionMethod(result: AbhaEnrolmentResult): AbhaAcquisitionMethod
  {
    if (this.mode === 'create' && result.isNew) {
      return 'AadhaarCreated';
    }

    return this.verifyAcquisitionMethod;
  }

  private startScanner(): void {
    setTimeout(() => this.initCamera(), 0);
  }

  private async initCamera(): Promise<void> {
    const video = this.qrVideo?.nativeElement;
    if (!video) {
      return;
    }

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      video.srcObject = this.mediaStream;
      await video.play();
      this.scanTimer = setInterval(() => this.decodeFrame(video), 400);
    } catch (error) {
      this.scanError = this.translate.instant(this.resolveCameraErrorKey(error));
    }
  }

  retryScan(): void {
    this.scanError = '';
    this.stopScanner();
    this.startScanner();
  }

  private resolveCameraErrorKey(error: unknown): string {
    const errorName = error instanceof DOMException ? error.name : '';
    if (errorName === 'NotAllowedError') {
      return 'ABHA.SCAN_PERMISSION_ERROR';
    }

    if (errorName === 'NotFoundError') {
      return 'ABHA.SCAN_NO_CAMERA';
    }

    return 'ABHA.SCAN_CAMERA_ERROR';
  }

  private decodeFrame(video: HTMLVideoElement): void {
    if (!video.videoWidth) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    context.drawImage(video, 0, 0);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const qrCode = jsQR(imageData.data, imageData.width, imageData.height);
    if (qrCode?.data) {
      this.handleQrText(qrCode.data);
    }
  }

  private handleQrText(qrText: string): void {
    const scanned = parseAbhaQr(qrText);
    if (!scanned) {
      this.scanError = this.translate.instant('ABHA.SCAN_INVALID');
      return;
    }

    this.stopScanner();

    if (hasCompleteAbhaProfile(scanned)) {
      this.acceptQrProfile(scanned);
    } else {
      this.mode = 'verify';
      this.verifyAcquisitionMethod = 'QrScan';
      this.txnId = '';
      this.scanError = '';
      const scannedId = scanned.abhaNumber || scanned.abhaAddress;
      this.verifyForm.patchValue({
        loginId: scannedId
      });
      this.verifyForm.get('otp')?.disable();
      this.toastr.success(this.translate.instant('ABHA.SCAN_PREFILLED'));
    }
  }

  private acceptQrProfile(scanned: AbhaEnrolmentResult): void {
    if (!this.data?.patientId) {
      this.completeQrAcquisition(scanned);
      return;
    }

    this.isEnrolling = true;
    this.abhaService
      .auditAcquisition({
        patientId: this.data.patientId,
        acquisitionMethod: 'QrScan'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => this.handleQrAuditResponse(response, scanned));
  }

  private handleQrAuditResponse(response: BaseRespone<boolean>, scanned: AbhaEnrolmentResult): void {
    this.isEnrolling = false;
    if (response && response.isSuccess && response.result) {
      this.completeQrAcquisition(scanned);
      return;
    }

    const message = this.resolveError(response, this.translate.instant('ABHA.CREATE_FAILED'));
    this.scanError = message;
    this.toastr.error(message);
  }

  private completeQrAcquisition(scanned: AbhaEnrolmentResult): void {
    if (this.closeIfAlreadyLinked(scanned)) {
      return;
    }

    this.enrolment = { ...scanned, acquisitionMethod: 'QrScan' };
    this.toastr.success(this.translate.instant('ABHA.VERIFIED_SUCCESS'));
  }

  private closeIfAlreadyLinked(result: AbhaEnrolmentResult): boolean {
    if (!result.linkedPatientNumber) {
      return false;
    }

    this.toastr.warning(this.translate.instant('ABHA.ALREADY_LINKED', {
      patientNumber: result.linkedPatientNumber
    }));
    this.dialogUtils.closeSidePanel(this.dialogRef, null);
    return true;
  }

  private stopScanner(): void {
    if (this.scanTimer) {
      clearInterval(this.scanTimer);
      this.scanTimer = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
  }

  private resolveError(response: BaseRespone<unknown> | null, fallback: string): string {
    if (response && response.errors && response.errors.length > 0) {
      return response.errors.join('\n');
    }

    if (response && response.message) {
      return response.message;
    }

    return fallback;
  }
}
